import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, API_ORIGIN, getAccess } from '@/api/client';
import { PartnerPregamePlayer } from '@/components/PartnerPregamePlayer';

export default function PartnerPregamePage() {
  const { briefId } = useParams();
  const navigate = useNavigate();
  const [importFile,setImportFile] = useState(null), [approved,setApproved] = useState(false);
  const base = `/admin/partner-briefs/${encodeURIComponent(briefId)}`;
  const [catalog,setCatalog] = useState(null), [ids,setIds] = useState([]);
  const [episode,setEpisode] = useState(null), [error,setError] = useState(''), [busy,setBusy] = useState(false);
  const [complete,setComplete] = useState(false), [saved,setSaved] = useState([]);
  const epoch = useRef(0);
  const invalidate = useCallback(()=>{epoch.current+=1;},[]);
  useEffect(() => {
    let active=true; invalidate(); setCatalog(null);setEpisode(null);setIds([]);setError('');setBusy(false);setComplete(false);setSaved([]);
    if (!briefId) return ()=>{active=false;invalidate();};
    api(`${base}/catalog`,{auth:true}).then(r=>{if(active)setCatalog(r);})
      .catch(()=>{if(active)setError('This private brief is unavailable to your account.');});
    api(`${base}/shows`,{auth:true}).then(r=>{if(active)setSaved(r.shows||[]);}).catch(()=>{if(active)setError('Saved shows could not be loaded.');});
    return ()=>{active=false;invalidate();};
  },[base,briefId,invalidate]);
  const preview = async (full=false) => {
    const current=epoch.current;
    setBusy(true);setError('');
    try { const r=await api(`${base}/recorded-script`,{auth:true,method:'POST',body:full?{}:{row_ids:ids}});if(current===epoch.current){setComplete(full);setEpisode(r.episode_preview);} }
    catch (_) {if(current===epoch.current)setError('The selected facts cannot form a pregame script.');}
    finally {if(current===epoch.current)setBusy(false);}
  };
  const generate = async () => {
    const current=epoch.current;
    setBusy(true);setError('');
    try {
      const r=await api(`${base}/audio`,{auth:true,method:'POST',body:complete?{}:{row_ids:ids},timeoutMs:480000});
      if(current!==epoch.current)return;
      const ready=await api(`${base}/audio/${r.audio_id}`,{auth:true});if(current===epoch.current){setEpisode(ready.episode);setSaved(old=>[{audio_id:r.audio_id,state:r.state},...old.filter(x=>x.audio_id!==r.audio_id)]);}
    } catch (_) {if(current===epoch.current)setError('Audio generation did not complete. No automatic retry was started.');}
    finally {if(current===epoch.current)setBusy(false);}
  };
  const reopen = async audioId => {
    const current=++epoch.current;setBusy(true);setError('');
    try {const r=await api(`${base}/audio/${encodeURIComponent(audioId)}`,{auth:true});if(current===epoch.current)setEpisode(r.episode);}
    catch(_){if(current===epoch.current)setError('Saved show is unavailable.');}
    finally{if(current===epoch.current)setBusy(false);}
  };
  const loadAudio = useCallback(async (audioId,index,signal)=>{
    const r=await fetch(`${API_ORIGIN}/api/v1${base}/audio/${encodeURIComponent(audioId)}/${index}`,
      {headers:{Authorization:`Bearer ${getAccess()}`},signal,cache:'no-store'});
    if(!r.ok) throw new Error('Private audio unavailable');
    return r.blob();
  },[base]);
  const importBrief = async () => {
    if (!importFile || !approved || busy) return;
    const current=epoch.current; setBusy(true);setError('');
    try {
      if (importFile.size > 1000000) throw new Error('File too large');
      const source=JSON.parse(await importFile.text());
      if(current!==epoch.current)return;
      if(source.schema_version!=='private-pregame-evidence-v1')throw new Error('Unsupported packet');
      const result=await api('/admin/partner-briefs',{auth:true,method:'POST',body:{source_evidence:source,version:1,external_processing_approved:true,voice_only:true}});
      if(current===epoch.current)navigate(`/admin/partner-show/${encodeURIComponent(result.brief_id)}`);
    } catch (_) {if(current===epoch.current)setError('Private import failed. Check the packet and administrator access. No audio was generated.');}
    finally {if(current===epoch.current)setBusy(false);}
  };
  return <main><h1>Partner pregame studio</h1>
    {!briefId && <section><h2>Import private game brief</h2>
      <label>Sanitized game packet<input type="file" accept="application/json,.json" disabled={busy} onChange={e=>{setImportFile(e.target.files?.[0]||null);setApproved(false);}} /></label>
      <label><input type="checkbox" checked={approved} disabled={busy} onChange={e=>setApproved(e.target.checked)} />I approve storing this sanitized packet privately and sending selected show text to the configured voice service when I explicitly generate audio.</label>
      <button disabled={busy||!importFile||!approved} onClick={importBrief}>Import private brief</button>
      <p>Import does not generate audio or publish a show.</p></section>}

    {error && <p role="alert">{error}</p>}
    {catalog && <><p>{catalog.source_title} — snapshot {catalog.source_date}. Not live data.</p>
      <button disabled={busy} onClick={()=>preview(true)}>Prepare complete pregame show</button>
      <p>A short Character Show with an opening, team context, character exchange and closing. Actual audio duration is measured after generation.</p>
      {saved.map(show=><button key={show.audio_id} disabled={busy||show.state!=='ready'} onClick={()=>reopen(show.audio_id)}>Open saved show ({show.state})</button>)}
      <p>Optional selection: choose 3-24 source facts for a custom recorded script.</p>
      {catalog.rows.map(r=><label key={r.id} className="block"><input type="checkbox" disabled={busy}
        checked={ids.includes(r.id)} onChange={e=>{setEpisode(null);setIds(old=>e.target.checked?[...old,r.id]:old.filter(i=>i!==r.id));}} />
        {r.team} / {r.subject}: {r.metric} {String(r.value)} (page {r.page})</label>)}
      <button disabled={busy||ids.length<3||ids.length>24} onClick={()=>preview(false)}>Preview script</button>
      {episode && <><p>Generating audio sends the selected script to the configured voice service and uses its normal charges. No automatic generation.</p>
        <button disabled={busy||!catalog.audio_enabled||episode.audio?.state==='ready'} onClick={generate}>{complete?'Produce complete pregame show':'Generate selected recorded audio'}</button>
        <PartnerPregamePlayer episode={episode} loadAudio={loadAudio} />
        <ol>{episode.lines.map(l=><li key={l.index}>{l.speaker_name}: {l.text}</li>)}</ol></>}
    </>}
  </main>;
}
